/**
 * 交易大厅（首页）
 * 搜索栏 + 快捷入口 + 品类分区 + 精选推荐 + 实时成交流（30s 轮询）+ 平台公告 + 行情入口
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTradeHall } from '@/api/trade';
import type { HallData } from '@/types';
import { CATEGORIES } from '@/utils/constants';
import { formatMoney, timeAgo } from '@/utils/format';
import CattleCard from '@/components/common/CattleCard';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import Empty from '@/components/common/Empty';
import '@/styles/pages/hall.css';

const QUICK_ENTRIES = [
  { key: 'buy', title: '我要买牛', desc: '浏览在售牛只，一口价/竞价灵活选择', icon: '🛒', to: '/market' },
  { key: 'sell', title: '我要卖牛', desc: '发布挂牌，平台审核快速上架', icon: '📢', to: '/listing/new' },
  { key: 'listing', title: '发布挂牌', desc: '单头/批量上架，交易设置一站式', icon: '🏷️', to: '/listing/new' },
  { key: 'auction', title: '竞价大厅', desc: '英式拍卖，实时出价防秒杀', icon: '🔨', to: '/auctions' },
];

export default function Hall() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [hall, setHall] = useState<HallData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getTradeHall();
      setHall(data);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 首次加载 + 30s 轮询实时成交流
  useEffect(() => {
    load();
    timerRef.current = window.setInterval(() => load(true), 30000);
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    };
  }, [load]);

  const onSearch = () => {
    const q = keyword.trim();
    navigate(q ? `/market?q=${encodeURIComponent(q)}` : '/market');
  };

  if (loading && !hall) {
    return (
      <div className="container hall-page">
        <Loading rows={6} />
      </div>
    );
  }

  if (error && !hall) {
    return (
      <div className="container hall-page">
        <ErrorBox message={error} onRetry={() => load()} />
      </div>
    );
  }

  const data = hall ?? {
    categories: CATEGORIES.map((c) => ({ ...c, count: 0 })),
    featured: [],
    recent_trades: [],
    notices: [],
    total_on_sale: 0,
  };

  return (
    <div className="hall-page">
      {/* 顶部搜索 + 快捷入口 */}
      <section className="hall-hero">
        <div className="container">
          <div className="hall-hero__title">
            山东牛产业智慧平台 · 牛只交易
          </div>
          <div className="hall-hero__sub">让每一头牛都有可信档案 · 让每一笔交易都有可信履约</div>
          <div className="hall-search">
            <input
              className="hall-search__input"
              placeholder="搜索品种 / 耳标号 / 养殖场"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <button className="btn btn-gold" onClick={onSearch}>搜索</button>
          </div>
          <div className="hall-quick">
            {QUICK_ENTRIES.map((item) => (
              <Link className="hall-quick__item" to={item.to} key={item.key}>
                <span className="hall-quick__icon">{item.icon}</span>
                <span className="hall-quick__text">
                  <strong>{item.title}</strong>
                  <em>{item.desc}</em>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="container">
        {/* 品类分区 + 成交流 */}
        <div className="hall-main">
          <div className="hall-left">
            <div className="section-head">
              <div>
                <h2>品类分区</h2>
                <p className="section-desc">当前在售 <b className="hall-total">{data.total_on_sale}</b> 头牛只</p>
              </div>
              <Link className="section-more" to="/market">查看全部 →</Link>
            </div>

            <div className="hall-categories">
              {data.categories.map((c) => (
                <Link className="hall-category" to={`/market?category=${encodeURIComponent(c.key)}`} key={c.key}>
                  <span className="hall-category__name">{c.name}</span>
                  <span className="hall-category__desc">{c.desc}</span>
                  <span className="hall-category__count">在售 {c.count} 头</span>
                </Link>
              ))}
            </div>

            <div className="section-head">
              <div>
                <h2>精选推荐</h2>
                <p className="section-desc">平台严选 · 溯源完整 · 检疫合格</p>
              </div>
              <Link className="section-more" to="/market">更多牛只 →</Link>
            </div>

            {data.featured.length > 0 ? (
              <div className="hall-featured">
                {data.featured.slice(0, 8).map((c) => (
                  <CattleCard cattle={c} key={c.id} />
                ))}
              </div>
            ) : (
              <Empty title="暂无精选牛只" desc="敬请期待运营配置的推荐牛只" />
            )}

            <div className="hall-quote-entry">
              <div>
                <strong>行情看板</strong>
                <p>今日均价 / 价格走势 / 品种对比 / 地区均价</p>
              </div>
              <Link className="btn btn-primary" to="/quotes">查看行情 →</Link>
            </div>
          </div>

          <aside className="hall-right">
            <div className="hall-panel">
              <div className="hall-panel__head">
                <span className="hall-panel__dot" />
                <strong>实时成交</strong>
                <em>30s 自动刷新</em>
              </div>
              {data.recent_trades.length > 0 ? (
                <ul className="hall-trades">
                  {data.recent_trades.map((t) => (
                    <li className="hall-trade" key={t.id}>
                      <span className="hall-trade__region">{t.region}</span>
                      <span className="hall-trade__breed">{t.breed}</span>
                      <span className="hall-trade__qty">{t.quantity}头</span>
                      <span className="hall-trade__price">{formatMoney(t.price)}</span>
                      <span className="hall-trade__time">{timeAgo(t.time)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty icon="📉" title="暂无成交" desc="看看在售牛只，抢先一步" />
              )}
            </div>

            <div className="hall-panel">
              <div className="hall-panel__head">
                <span className="hall-panel__dot hall-panel__dot--gold" />
                <strong>平台公告</strong>
              </div>
              {data.notices.length > 0 ? (
                <ul className="hall-notices">
                  {data.notices.map((n) => (
                    <li className="hall-notice" key={n.id}>
                      <span className="hall-notice__title">{n.title}</span>
                      <span className="hall-notice__time">{timeAgo(n.created_at)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
